import { Button, Divider, IconPlusCircle, Tooltip } from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { AvailableLayoutModule, Brand, ItpIssuePageType, ModuleContainer, PageContainer } from 'data/generated/graphql';
import styles from './AddITPPageButton.module.scss';

interface AddITPPageButtonProps {
  addableUiModules?: AvailableLayoutModule[];
  availableTreatmentTypes?: string[];
  nextPageIndex: number;
  sectionIndex?: number;
  itpPageType?: ItpIssuePageType | null;
}

export const AddITPPageButton = ({
  addableUiModules,
  availableTreatmentTypes,
  nextPageIndex,
  sectionIndex,
  itpPageType
}: AddITPPageButtonProps) => {
  const { insertEntity } = useDataModelContext();

  const handleAddPage = () => {
    if (!addableUiModules || (addableUiModules.length === 0 && !availableTreatmentTypes?.length)) {
      return;
    }

    const defaultModule =
      addableUiModules.find((module) => {
        const itpAllowedTreatmentTypes = [
          // Treatment types using enum key
          'ITP_12_STORIES',
          'ITP_2_STORIES',
          'ITP_2_STORIES_WITH_FEATURE_SPOT',
          'ITP_3_STORIES_WITH_FEATURE_SPOT',
          'ITP_5_STORIES_WITH_FEATURE_SPOT',
          'ITP_6_STORIES',
          'ITP_6_STORIES_WITH_FEATURE_SPOT',
          // Treatment types using enum value
          'CONTINUATION-generic-12_template.xml',
          'FRONT-2Story-no-ad_template.xml',
          'UNIVERSAL-2story-feature-no-ad_template.xml',
          'FRONT-3story-feature-ad_template.xml',
          'CONTINUATION-4story-deco-ad_template.xml',
          'FRONT-6story-ad_template.xml',
          'FRONT-6story-ad-liondoor_template.xml'
        ];
        return module.defaultModule.uiModuleFields?.titleModule?.allowedTreatmentTypes?.every((tt) =>
          itpAllowedTreatmentTypes.includes(tt)
        );
      })?.defaultModule ?? null;

    if (!defaultModule) return;

    const newModule: ModuleContainer = {
      type: 'Module',
      uuid: uuidv4(),
      attributes: {
        pageModule: defaultModule,
        brand: itpPageType === ItpIssuePageType.Opinion ? Brand.Wsjopinion : Brand.Wsj
      },
      collection: []
    };

    const newPage: PageContainer = {
      type: 'Page',
      uuid: uuidv4(),
      // The following layoutId comes from this backend file: src/services/pageTypeSetting/settings/wsj/itp.ts
      attributes: { layoutId: 'default' },
      collection: [newModule]
    };

    insertEntity(`0-${sectionIndex}-${nextPageIndex}`, newPage);
  };

  if (!addableUiModules || (addableUiModules.length === 0 && !availableTreatmentTypes?.length)) {
    return null;
  }

  return (
    <div className={styles.blockDivider}>
      <div className={styles.blockDividerContent}>
        <Divider className={styles.blockDividerDivider} />
        <Tooltip>
          <Tooltip.Content position={nextPageIndex === 0 ? 'bottom' : 'top'}>Add a Module Here</Tooltip.Content>
          <Tooltip.Trigger>
            <Button tertiary icon={IconPlusCircle} onClick={handleAddPage} data-testid="add-module-button" />
          </Tooltip.Trigger>
        </Tooltip>
        <Divider className={styles.blockDividerDivider} />
      </div>
    </div>
  );
};
